import React, { useState, useRef } from "react";
import { IoMdClose } from "react-icons/io";
import { TbArrowBackUp, TbDeviceFloppy } from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import { useThemeManager } from "../hooks/useThemeManager";
import { useCss } from "../context/CssContext";
import ThemeSection from "./ThemeSection";
import SuccessAlert from "./alerts/SuccessAlert";
import ErrorAlert from "./alerts/ErrorAlert";
import config from "../config";
import "./CssPanel.css";

const CssPanelNew = ({ open, onClose }) => {
  const { themeValues, themeConfig, flatConfig, updateThemeValue, resetToDefaults } =
    useThemeManager();
  const {
    fetchAndApplyCss,
    customBgImage,
    customBgEnabled,
    setCustomBgEnabled,
    updateCustomBg,
    removeCustomBg,
  } = useCss();

  const [loading, setLoading] = useState(false);
  const [bgUploading, setBgUploading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const bgFileInputRef = useRef(null);

  // Alert mesajlarını otomatik temizle
  React.useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  React.useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  if (!open) return null;

  // Görseli sıkıştır (max 900px, %55 JPEG)
  const compressBgImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX_W = 1920;
        let { width, height } = img;
        if (width > MAX_W) {
          height = Math.round(height * MAX_W / width);
          width = MAX_W;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const handleBgFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Lütfen geçerli bir görsel dosyası seçin');
      return;
    }

    setBgUploading(true);
    try {
      const compressed = await compressBgImage(file);
      updateCustomBg(compressed);
      setSuccessMessage('Arkaplan görseli yüklendi — kaydetmek için "CSS Kaydet" butonuna basın');
    } catch {
      setErrorMessage('Görsel işlenirken bir hata oluştu');
    } finally {
      setBgUploading(false);
    }
  };

  // CSS verilerini API'ye kaydet
  const saveCssToApi = async () => {
    setLoading(true);

    try {
      const cssData = {};

      // CSS değişken değerlerini ekle
      Object.keys(flatConfig).forEach((key) => {
        const cfg = flatConfig[key];
        if (cfg && cfg.cssVar) {
          cssData[cfg.cssVar] = themeValues[key];
        }
      });

      // Arkaplan görseli alanlarını ekle
      cssData['customBgImage'] = customBgImage ?? null;
      cssData['customBgEnabled'] = customBgEnabled ?? false;

      const response = await fetch(`${config.adminApi}/set_css`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: cssData }),
        mode: 'cors',
        credentials: 'include',
      });

      const result = await response.json();

      if (response.ok && result.status) {
        setSuccessMessage(result.message || "CSS ayarları başarıyla kaydedildi!");
        setErrorMessage('');
        await fetchAndApplyCss();
      } else {
        setErrorMessage(result.message || 'CSS kaydetme hatası oluştu');
        setSuccessMessage('');
      }
    } catch (error) {
      console.error('CSS kaydetme hatası:', error);
      setErrorMessage("Bağlantı hatası! Lütfen tekrar deneyin.");
      setSuccessMessage('');
    } finally {
      setLoading(false);
      setShowSaveModal(false);
    }
  };

  // Varsayılan ayarlara sıfırla (onaylı)
  const handleConfirmedReset = () => {
    resetToDefaults();
    setShowResetModal(false);
    setSuccessMessage("Ayarlar varsayılan değerlere sıfırlandı!");
    setErrorMessage('');
  };

  // Bölüm başlıkları
  const sectionTitles = {
    general: "Genel Ayarlar",
    header: "Header Ayarları",
    mainCategory: "Ana Kategori Ayarları",
    tab: "Tab Ayarları",
    bonusButtons: "Bonus Kart Butonları",
    rulesPopup: "Kurallar Popup'ı",
    historyPopup: "Geçmiş Popup'ı",
    multipleBonusDialog: "Çoklu Bonus Dialog'u",
    bonusCard: "Bonus Kart'ı",
    bonusConfirmPopup: "Bonus Onay Popup'ı",
    loadingPage: "Loading Sayfası Ayarları",
  };

  return (
    <div className="css-panel">
      {/* Alert Mesajları */}
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <ErrorAlert message={errorMessage} />}

      <div className="css-panel-header">
        <h2>CSS Düzenleme Paneli</h2>
        <div className="header-actions">
          <button
            className="save-button"
            onClick={() => setShowSaveModal(true)}
            disabled={loading || bgUploading}
            title="CSS ayarlarını kaydet"
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#22c55e' }} />
            ) : (
              <TbDeviceFloppy size={20} />
            )}
            CSS Kaydet
          </button>
          <button
            className="reset-button"
            onClick={() => setShowResetModal(true)}
            disabled={loading || bgUploading}
            title="Varsayılan ayarlara dön"
          >
            <TbArrowBackUp size={20} />
            Sıfırla
          </button>
          <button className="close-button" onClick={onClose}>
            <IoMdClose size={24} />
          </button>
        </div>
      </div>

      <div className="css-panel-content">

        {/* ── Arkaplan Görseli Bölümü ── */}
        <div className="bg-upload-section">
          <div className="bg-section-title">Arkaplan Görseli</div>

          <div className="bg-preview-box">
            {customBgImage ? (
              <img src={customBgImage} alt="Arkaplan" className="bg-preview-img" />
            ) : (
              <div className="bg-preview-empty">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="M21 15l-5-5L5 21" />
                </svg>
                <span>Henüz görsel yüklenmedi</span>
              </div>
            )}
          </div>

          <div className="bg-controls-row">
            <input
              ref={bgFileInputRef}
              type="file"
              accept="image/*"
              onChange={handleBgFileChange}
              style={{ display: 'none' }}
            />
            <button
              className={`bg-select-btn${bgUploading ? ' loading' : ''}`}
              onClick={() => bgFileInputRef.current?.click()}
              disabled={bgUploading}
            >
              {bgUploading ? (
                <><span className="bg-spinner" /> Yükleniyor...</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Görsel Seç
                </>
              )}
            </button>
            {customBgImage && (
              <button
                className="bg-remove-btn"
                onClick={removeCustomBg}
                disabled={bgUploading}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                </svg>
                Kaldır
              </button>
            )}
          </div>

          <div
            className="bg-toggle-row"
            style={{ pointerEvents: bgUploading ? 'none' : 'auto' }}
            onClick={() => setCustomBgEnabled(!customBgEnabled)}
          >
            <div className={`bg-toggle-switch${customBgEnabled ? ' on' : ''}`}>
              <div className="bg-toggle-knob" />
            </div>
            <span className="bg-toggle-label">
              {customBgEnabled ? 'Özel Arkaplan Aktif' : 'Özel Arkaplan Devre Dışı'}
            </span>
          </div>
        </div>
        {/* ── /Arkaplan Görseli ── */}

        {Object.entries(themeConfig).map(([sectionKey, properties]) => (
          <ThemeSection
            key={sectionKey}
            title={sectionTitles[sectionKey]}
            sectionKey={sectionKey}
            properties={properties}
            themeValues={themeValues}
            onThemeChange={updateThemeValue}
            defaultExpanded={sectionKey === "general"}
          />
        ))}
      </div>

      {/* CSS Kaydet Onay Modal */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>CSS Ayarlarını Kaydet</h3>
            </div>
            <div className="modal-content">
              <p>Mevcut CSS ayarlarını kaydetmek istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                Bu işlem sunucudaki CSS ayarlarını güncelleyecektir.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowSaveModal(false)}
                disabled={loading}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button"
                onClick={saveCssToApi}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                    Kaydediliyor...
                  </>
                ) : (
                  'Evet, Kaydet'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Varsayılana Sıfırla Onay Modal */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Varsayılan Ayarlara Sıfırla</h3>
            </div>
            <div className="modal-content">
              <p>Tüm CSS ayarlarını varsayılan değerlere sıfırlamak istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '14px', color: '#e74c3c', marginTop: '8px' }}>
                ⚠️ Bu işlem geri alınamaz! Mevcut ayarlarınız kaybolacaktır.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowResetModal(false)}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button reset"
                onClick={handleConfirmedReset}
              >
                Evet, Sıfırla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CssPanelNew;
